import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { isAdmin } from "../../utils/permissions";

const INITIAL_ERRORS = { uname: "", uemail: "", uis_active: "" };

const API = window.MANTLE_CONFIG?.API_URL ?? "http://localhost:5000/api";
const authHeaders = () => ({
  Authorization: "Bearer " + localStorage.getItem("jwt"),
  "x-db-name": window.MANTLE_CONFIG?.TENANT_DB,
});

/* Disable the 'Active' checkbox if the user being edited is not an administrator or is the current user */
function disableCheckbox (user) {
  let disabled = false;
  let currentUID = parseInt(localStorage.getItem('uid'));
  
  
  if (!isAdmin()) {
    disabled = true;
  } else if (user.uid === currentUID) {
    disabled = true;
  }
  
  return disabled;
}

function validate({ uname, uemail }) {
  const errors = {};
  
  if (!uname.trim()) errors.uname = "Username is required";
  else if (!/^[a-z0-9_]{4,25}$/.test(uname.trim()))
    errors.uname = "Username must be 4–25 lowercase letters, numbers, or underscores";
  if (!uemail.trim()) errors.uemail = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(uemail.trim()))
    errors.uemail = "Enter a valid email address";
    
  return errors;
}

export default function UserEditModal({ user, onSave, onClose, onChangePassword }) {
  const [form, setForm] = useState({ uname: user.uname, uemail: user.uemail, uis_active: user.uis_active });
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  // Admin-only password reset
  const [pwd, setPwd] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  const canResetPassword = isAdmin() && typeof onChangePassword === "function";

  // ---- Roles & Sites (admin-only) ----------------------------------------
  const canManageRoles = isAdmin();
  const [roles, setRoles] = useState([]);
  const [sites, setSites] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [addUrid, setAddUrid] = useState("");
  const [addSite, setAddSite] = useState(""); // "" = All sites
  const [raError, setRaError] = useState("");
  const [raBusy, setRaBusy] = useState(false);

  const reloadAssignments = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users/user-roles/${user.uid}`, { headers: authHeaders() });
      setAssignments(r.data?.roles || []);
    } catch {
      /* leave list as-is */
    }
  }, [user.uid]);

  useEffect(() => {
    if (!canManageRoles) return;
    axios.get(`${API}/users/all-roles`, { headers: authHeaders() })
      .then((r) => setRoles((r.data || []).filter((x) => x.active !== false)))
      .catch(() => {});
    axios.get(`${API}/sites/load-all`, { headers: authHeaders() })
      .then((r) => setSites(r.data || []))
      .catch(() => {});
    reloadAssignments();
  }, [canManageRoles, reloadAssignments]);

  const siteName = (code) => {
    if (!code) return "All sites";
    return sites.find((s) => s.code === code)?.name || code;
  };

  async function handleAddAssignment() {
    setRaError("");
    if (!addUrid) return setRaError("Select a role.");
    try {
      setRaBusy(true);
      await axios.post(
        `${API}/users/role-assignment`,
        { uid: user.uid, urid: Number(addUrid), site_code: addSite || null },
        { headers: authHeaders() }
      );
      setAddUrid("");
      setAddSite("");
      await reloadAssignments();
    } catch (err) {
      setRaError(err?.response?.data?.err || "Failed to add the role/site assignment.");
    } finally {
      setRaBusy(false);
    }
  }

  async function handleRemoveAssignment(a) {
    setRaError("");
    try {
      setRaBusy(true);
      await axios.delete(`${API}/users/role-assignment`, {
        headers: authHeaders(),
        data: { uid: user.uid, urid: a.urid, site_code: a.site_code || null },
      });
      await reloadAssignments();
    } catch (err) {
      setRaError(err?.response?.data?.err || "Failed to remove the assignment.");
    } finally {
      setRaBusy(false);
    }
  }

  async function handleSetPassword() {
    setPwdError("");
    setPwdMsg("");
    if (!pwd || !pwdConfirm) return setPwdError("Enter and confirm the new password.");
    if (pwd !== pwdConfirm) return setPwdError("Passwords do not match.");
    if (pwd.length < 12) return setPwdError("Password must be at least 12 characters long.");

    try {
      setPwdBusy(true);
      await onChangePassword(user.uid, pwd, pwdConfirm);
      setPwd("");
      setPwdConfirm("");
      setPwdMsg("Password updated successfully.");
    } catch (err) {
      setPwdError(err?.response?.data?.err || err.message || "Failed to change password.");
    } finally {
      setPwdBusy(false);
    }
  }

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  function handleChange(e) {
    const { name, value } = e.target;
    
    
    const newValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    setForm((prev) => ({ ...prev, [name]: newValue }));
    
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  }

  function handleSave() {
    const errs = validate(form);
    if (Object.keys(errs).length) return setErrors(errs);
    
    let update = { ...user, uname: form.uname.trim(), uemail: form.uemail.trim(), uis_active: form.uis_active };
    
    onSave (update);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-xl shadow-lg w-full max-w-lg flex flex-col max-h-[90vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-700 shrink-0">
          <div>
            <p id="modal-title" className="font-medium text-gray-900 dark:text-slate-100">Edit/Update User</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              ID #{String(user.uid).padStart(4, "0")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4 flex-1 overflow-y-auto">
          <Field label="Uname" error={errors.uname}>
            <input
              name="uname"
              value={form.uname}
              onChange={handleChange}
              autoComplete="off"
              className={inputClass (errors.uname)}
            />
          </Field>
          <Field label="Email address" error={errors.uemail}>
            <input
              name="uemail"
              type="email"
              value={form.uemail}
              onChange={handleChange}
              autoComplete="off"
              className={inputClass (errors.uemail)}
            />
          </Field>
          <Field label="Is Active" error={errors.uis_active}>
            <input
              name="uis_active"
              type="checkbox"
              value={form.uis_active}
              onChange={handleChange}
              defaultChecked={form.uis_active}
              autoComplete="off"
              className={inputClass (errors.uis_active)}
              disabled={disableCheckbox(user)}
            />
          </Field>

          {canResetPassword && (
            <div className="pt-4 mt-1 border-t border-gray-200 dark:border-slate-700">
              <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Reset Password
              </p>
              <div className="space-y-3">
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="New password (min 12 characters)"
                  value={pwd}
                  onChange={(e) => { setPwd(e.target.value); setPwdError(""); setPwdMsg(""); }}
                  className={inputClass(pwdError)}
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  value={pwdConfirm}
                  onChange={(e) => { setPwdConfirm(e.target.value); setPwdError(""); setPwdMsg(""); }}
                  className={inputClass(pwdError)}
                />
                {pwdError && <p className="text-xs text-red-500">{pwdError}</p>}
                {pwdMsg && <p className="text-xs text-green-600">{pwdMsg}</p>}
                <button
                  type="button"
                  onClick={handleSetPassword}
                  disabled={pwdBusy}
                  className="px-3 py-2 text-sm border border-gray-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pwdBusy ? "Setting…" : "Set Password"}
                </button>
              </div>
            </div>
          )}

          {canManageRoles && (
            <div className="pt-4 mt-1 border-t border-gray-200 dark:border-slate-700">
              <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Roles &amp; Sites
              </p>

              {assignments.length === 0 ? (
                <p className="text-xs text-gray-400 mb-2">No roles assigned yet.</p>
              ) : (
                <ul className="mb-3 space-y-1.5">
                  {assignments.map((a) => (
                    <li
                      key={`${a.urid}-${a.site_code || "all"}`}
                      className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-1.5 text-sm"
                    >
                      <span className="text-slate-700 dark:text-slate-200 min-w-0 truncate">
                        <span className="font-medium">{a.ur_name}</span>
                        <span className="text-gray-400"> @ </span>
                        {siteName(a.site_code)}
                        {a.is_default_context && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-600">default</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAssignment(a)}
                        disabled={raBusy}
                        className="shrink-0 text-gray-400 hover:text-red-600 text-lg leading-none disabled:opacity-50"
                        aria-label="Remove assignment"
                        title="Remove"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center gap-2">
                <select
                  value={addUrid}
                  onChange={(e) => { setAddUrid(e.target.value); setRaError(""); }}
                  className={raSelectClass}
                >
                  <option value="">Select role…</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <select
                  value={addSite}
                  onChange={(e) => setAddSite(e.target.value)}
                  className={raSelectClass}
                >
                  <option value="">All sites</option>
                  {sites.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={handleAddAssignment}
                  disabled={raBusy || !addUrid}
                  className="shrink-0 px-3 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              {raError && <p className="mt-1 text-xs text-red-500">{raError}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-slate-700 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function inputClass(error) {
  return `w-full px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 ${
    error ? "border-red-400 focus:border-red-400" : "border-gray-200 dark:border-slate-600 focus:border-blue-400"
  }`;
}

const raSelectClass =
  "flex-1 min-w-0 px-2 py-2 text-sm border rounded-lg outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 border-gray-200 dark:border-slate-600 focus:border-blue-400";
